import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken } from '../utils/authSession';
import { getUploadUrl, toUploadPath } from '../utils/uploadUrl';

function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [letterFilter, setLetterFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    alias: '',
    category: 'general_care',
    description: '',
    image: '',
    operating_days: '',
    operating_hours: '',
    status: 'active'
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const navigate = useNavigate();

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchServices();
  }, [navigate, search, categoryFilter, letterFilter]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const token = getAccessToken();
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (categoryFilter) params.append('category', categoryFilter);
      if (letterFilter) params.append('letter', letterFilter);
      params.append('include_inactive', 'true'); // Include inactive services for admin

      const response = await axios.get(`/api/services?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setServices(response.data.services);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const token = getAccessToken();
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await axios.post('/api/upload', uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const imagePath = toUploadPath(response.data.filename || response.data.path || response.data.imageUrl);
      setFormData(prevFormData => ({ ...prevFormData, image: imagePath }));
    } catch (error) {
      alert('Failed to upload image');
      console.error('Error uploading image:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      alias: '',
      category: 'general_care',
      description: '',
      image: '',
      operating_days: '',
      operating_hours: '',
      status: 'active'
    });
    setFormError('');
    setShowAddModal(true);
  };

  const handleEdit = async (service) => {
    try {
      // Fetch full service data to ensure we have all fields
      const token = getAccessToken();
      const response = await axios.get(`/api/services/${service.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const fullService = response.data;
      
      setEditingService(fullService);
      setFormData({
        name: fullService.name || '',
        alias: fullService.alias || '',
        category: fullService.category || 'general_care',
        description: fullService.description || '',
        image: fullService.image || '',
        operating_days: fullService.operating_days || '',
        operating_hours: fullService.operating_hours || '',
        status: fullService.status || 'active'
      });
      setFormError('');
      setShowEditModal(true);
    } catch (error) {
      console.error('Error fetching service details:', error);
      // Fallback to using the service data we have
      setEditingService(service);
      setFormData({
        name: service.name || '',
        alias: service.alias || '',
        category: service.category || 'general_care',
        description: service.description || '',
        image: service.image || '',
        operating_days: service.operating_days || '',
        operating_hours: service.operating_hours || '',
        status: service.status || 'active'
      });
      setFormError('');
      setShowEditModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      // Trim whitespace from form data
      const trimmedName = (formData.name || '').trim();
      const trimmedData = {
        ...formData,
        name: trimmedName,
        alias: (formData.alias || '').trim() || null,
        description: (formData.description || '').trim() || null,
        operating_days: (formData.operating_days || '').trim() || null,
        operating_hours: (formData.operating_hours || '').trim() || null
      };

      // Validate required fields
      if (!trimmedName || trimmedName.length === 0) {
        setFormError('Service name is required');
        setFormLoading(false);
        return;
      }

      // Ensure category has a default value
      if (!trimmedData.category) {
        trimmedData.category = 'general_care';
      }

      const token = getAccessToken();
      const data = trimmedData;

      if (editingService) {
        await axios.put(`/api/services/${editingService.id}`, data, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        alert('Service updated successfully!');
      } else {
        await axios.post('/api/services', data, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        alert('Service created successfully!');
      }

      setShowAddModal(false);
      setShowEditModal(false);
      setEditingService(null);
      await fetchServices();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to save service';
      setFormError(errorMessage);
      
      if (error.response?.status === 401) {
        alert('Your session has expired. Please log in again.');
        navigate('/admin/login');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      const token = getAccessToken();
      await axios.delete(`/api/services/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      alert('Service deleted successfully!');
      fetchServices();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete service');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container-fluid py-4">
        <div className="row mb-4">
          <div className="col">
            <h1 className="h3 mb-0">Services Management</h1>
          </div>
          <div className="col-auto">
            <button className="btn btn-primary" onClick={handleAdd}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Service
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-md-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search services..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              <option value="general_care">General Care</option>
              <option value="diagnostic">Diagnostic</option>
              <option value="specialized">Specialized</option>
            </select>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={letterFilter}
              onChange={(e) => setLetterFilter(e.target.value)}
            >
              <option value="">All Letters</option>
              {alphabet.map(letter => (
                <option key={letter} value={letter}>{letter}</option>
              ))}
              <option value="#">#</option>
            </select>
          </div>
        </div>

        {/* Services Table */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Alias</th>
                    <th>Category</th>
                    <th>First Letter</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {services.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        No services found
                      </td>
                    </tr>
                  ) : (
                    services.map((service) => (
                      <tr key={service.id}>
                        <td>
                          {service.image ? (
                            <img 
                              src={service.image} 
                              alt={service.name}
                              style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                            />
                          ) : (
                            <div style={{ width: '50px', height: '50px', backgroundColor: '#e0e0e0', borderRadius: '4px' }}></div>
                          )}
                        </td>
                        <td>{service.name}</td>
                        <td>{service.alias || '-'}</td>
                        <td>
                          <span className="badge bg-info">
                            {service.category === 'general_care' ? 'General Care' : 
                             service.category === 'diagnostic' ? 'Diagnostic' : 'Specialized'}
                          </span>
                        </td>
                        <td>{service.first_letter}</td>
                        <td>
                          <span className={`badge ${service.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                            {service.status}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary me-2"
                            onClick={() => handleEdit(service)}
                          >
                            <i className="bi bi-pencil me-1"></i>
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(service.id)}
                          >
                            <i className="bi bi-trash me-1"></i>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Add Modal */}
        {showAddModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add New Service</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowAddModal(false)}
                  ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    {formError && (
                      <div className="alert alert-danger">{formError}</div>
                    )}
                    <div className="mb-3">
                      <label className="form-label">Service Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        autoComplete="off"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Alias</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.alias}
                        onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Category *</label>
                      <select
                        className="form-select"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        required
                      >
                        <option value="general_care">General Care</option>
                        <option value="diagnostic">Diagnostic</option>
                        <option value="specialized">Specialized</option>
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Image</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                      {uploadingImage && <small className="text-muted">Uploading...</small>}
                      {formData.image && (
                        <div className="mt-2">
                          <img 
                            src={getUploadUrl(formData.image)} 
                            alt="Preview" 
                            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Operating Days</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g., Monday - Friday, Monday - Saturday"
                        value={formData.operating_days}
                        onChange={(e) => setFormData({ ...formData, operating_days: e.target.value })}
                      />
                      <small className="text-muted">Enter the days the service is available</small>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Operating Hours</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g., 8:00 AM - 5:00 PM, 24/7"
                        value={formData.operating_hours}
                        onChange={(e) => setFormData({ ...formData, operating_hours: e.target.value })}
                      />
                      <small className="text-muted">Enter the hours the service is available</small>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowAddModal(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={formLoading}>
                      {formLoading ? 'Saving...' : 'Save Service'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Service</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowEditModal(false)}
                  ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    {formError && (
                      <div className="alert alert-danger">{formError}</div>
                    )}
                    <div className="mb-3">
                      <label className="form-label">Service Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        autoComplete="off"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Alias</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.alias}
                        onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Category *</label>
                      <select
                        className="form-select"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        required
                      >
                        <option value="general_care">General Care</option>
                        <option value="diagnostic">Diagnostic</option>
                        <option value="specialized">Specialized</option>
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Image</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                      {uploadingImage && <small className="text-muted">Uploading...</small>}
                      {formData.image && (
                        <div className="mt-2">
                          <img 
                            src={getUploadUrl(formData.image)} 
                            alt="Preview" 
                            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Operating Days</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g., Monday - Friday, Monday - Saturday"
                        value={formData.operating_days}
                        onChange={(e) => setFormData({ ...formData, operating_days: e.target.value })}
                      />
                      <small className="text-muted">Enter the days the service is available</small>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Operating Hours</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g., 8:00 AM - 5:00 PM, 24/7"
                        value={formData.operating_hours}
                        onChange={(e) => setFormData({ ...formData, operating_hours: e.target.value })}
                      />
                      <small className="text-muted">Enter the hours the service is available</small>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowEditModal(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={formLoading}>
                      {formLoading ? 'Updating...' : 'Update Service'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminServices;
